import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useCreateWorker, useUpdateWorker, useWorker } from '@/hooks/useWorkers';
import { WorkerForm } from '@/components/workers/WorkerForm';
import type { WorkerInput } from '@jobos/shared/types/worker';

export default function WorkerFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { data: worker, isLoading: isLoadingWorker } = useWorker(id);
  const { mutateAsync: createWorker, isPending: isCreating } = useCreateWorker();
  const { mutateAsync: updateWorker, isPending: isUpdating } = useUpdateWorker(id || '');

  const handleSubmit = async (data: WorkerInput) => {
    try {
      if (isEditMode) {
        await updateWorker(data);
      } else {
        await createWorker(data);
      }
      navigate('/settings/workers');
    } catch (error) {
      console.error('Failed to save worker:', error);
    }
  };

  const handleCancel = () => {
    navigate('/settings/workers');
  };

  if (isEditMode && isLoadingWorker) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEditMode ? 'Edit Worker' : 'Add New Worker'}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {isEditMode
            ? 'Update worker information and calendar settings'
            : 'Add a new worker for timesheet tracking'}
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <WorkerForm
          onSubmit={handleSubmit}
          defaultValues={worker}
          isPending={isCreating || isUpdating}
          submitButtonText={isEditMode ? 'Update Worker' : 'Add Worker'}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
}
